import React from 'react'
import PropTypes from 'prop-types'

import { googleLoginApi, googleLoginApiValidation } from './googleLoginApi'
import GoogleSignInSDK from './GoogleSignInSDK'
import { GoogleOAuthProvider } from '@react-oauth/google'
import {
  getModalLoginExtraData,
  isAccountRevalidationExperimentOn,
} from '../../../util/experiments'
import {
  GOOGLE_LOGIN_METHOD,
  LOGIN_METHOD_KEY,
  MANDATORY_TIPIFICATION_KEY,
} from '../../../util/global.consts'
import storage from '../../../util/storage'
import { isValidMustTypify } from '../../../util/account'

const CLIENT_ID = '478370423031-748nbrdqm97onrnabd504u24bdd3nim1.apps.googleusercontent.com'

const GoogleSignIn = ({
  onCancel,
  onError,
  onSubmit,
  onSuccess,
  lurker,
  tracker,
  onPhoneNotValidatedError,
  onChallengePinCodeCall,
  sessionTokens,
  onAccountNotValidated,
  onAccountMustValidate,
  abTests,
  modalMode,
  onAccountSocialLoginTyping,
  onAccountCompleteData,
}) => {
  const viewMessageEvt = (objectDetail, data) =>
    lurker && lurker?.viewMessageEvt(objectDetail, data)

  const sendTrackerEvent = (event, data) => tracker && tracker?.viewMessageEvt(event, data)

  const clickEvt = () => {
    const extraData = getModalLoginExtraData(abTests, modalMode)
    lurker && lurker?.clickEvt('google', extraData)
  }

  const ERROR_REASONS = {
    ACCOUNT_NOT_VALIDATED: (error, extraData) => {
      const token = error.data?.token

      onAccountNotValidated(token)

      viewMessageEvt('error', {
        reason: `'google_account_not_validated`,
        accounts_status_code: error.status,
        ...extraData,
      })
    },
    PHONE_NOT_VALIDATED: error => {
      const token = error?.data?.token
      lurker.redirectEvt('phone_validation')

      if (error?.data?.accountJustCreated) {
        lurker.viewMessageEvt('signup_google_no_document')
      }

      storage.set(LOGIN_METHOD_KEY, GOOGLE_LOGIN_METHOD)
      onPhoneNotValidatedError(token)

      onError && onError('api_error')
    },
    SOCIAL_TYPIFY: error => {
      if (onAccountSocialLoginTyping) {
        const { email, token } = error?.data
        onAccountSocialLoginTyping(email, 'google', token)
      }
    },
    PIN_CODE: error => {
      const { response } = error

      lurker.redirectEvt('pin_code', {
        type: response?.data?.send?.type,
      })

      storage.set(LOGIN_METHOD_KEY, GOOGLE_LOGIN_METHOD)
      onChallengePinCodeCall(error.data)

      onError && onError('api_error')
    },
    ACCOUNT_TYPING_REQUIRED: error => {
      lurker.viewMessageEvt('error', {
        accounts_reason: 'ACCOUNT_TYPING_REQUIRED'.toLowerCase(),
        accounts_status_code: error.status,
        login_type: 'google',
      })

      storage.set(MANDATORY_TIPIFICATION_KEY, true)

      if (onAccountCompleteData) {
        onAccountCompleteData(error?.data?.token)
      }
    },
    ACCOUNT_MUST_VALIDATE: error => {
      lurker.viewMessageEvt('error', {
        accounts_reason: 'ACCOUNT_MUST_VALIDATE'.toLowerCase(),
        accounts_status_code: error.status,
        login_type: 'google',
      })
      onAccountMustValidate(error?.data?.token)
    },
    default: (error, extraData) => {
      const message = error?.message || 'unknown'

      viewMessageEvt('error', {
        reason: 'google_unexpected_error',
        accounts_error_message: message,
        ...extraData,
      })

      onError && onError('api_error')
    },
  }

  const onSuccessSignIn = async credential => {
    const googleLoginApiCaller = isAccountRevalidationExperimentOn(abTests)
      ? googleLoginApiValidation(credential, sessionTokens)
      : googleLoginApi(credential, sessionTokens, abTests)

    const isMustTypify = await isValidMustTypify()

    const extraData = getModalLoginExtraData(abTests, modalMode)

    googleLoginApiCaller
      .then(async ({ data }) => {
        const { remoteAddress, accountJustCreated, accountId, publicAccountId, isTypified } = data
        lurker.setPublicAccountId(publicAccountId)

        if (accountJustCreated) {
          if (window) {
            const { dataLayer } = window
            if (dataLayer) {
              dataLayer.push({ event: 'gg_signup_success', user_id: publicAccountId })
            }
          }

          viewMessageEvt('google_signup_success', {
            remote_addr: remoteAddress,
          })

          sendTrackerEvent('sign_up_google', {
            remote_addr: remoteAddress,
          })
        } else {
          viewMessageEvt('google_login_success', {
            remote_addr: remoteAddress,
            accountId: accountId,
            ...extraData,
          })

          if (!isTypified && isMustTypify) {
            return onAccountCompleteData()
          }
        }
        onSuccess && onSuccess({ ...data, method: GOOGLE_LOGIN_METHOD })
      })
      .catch(({ response }) => {
        const extraData = getModalLoginExtraData(abTests, modalMode)
        const handlerError = ERROR_REASONS[response?.data?.message] || ERROR_REASONS['default']

        handlerError(response, extraData)
      })
  }

  const onFailure = error => {
    const { code = 'unknown', message = 'unknown' } = error

    viewMessageEvt('error', {
      reason: code,
      accounts_error_message: message,
    })

    onError && onError(code)
  }

  const onCancelSignIn = (error = 'unknown') => {
    viewMessageEvt('error', {
      reason: 'google_cancelled_by_user',
      accounts_error_message: error,
    })

    onCancel && onCancel()
  }

  return (
    <GoogleOAuthProvider clientId={CLIENT_ID}>
      <GoogleSignInSDK
        onSuccess={onSuccessSignIn}
        onError={onFailure}
        onCancel={onCancelSignIn}
        onClick={clickEvt}
      />
    </GoogleOAuthProvider>
  )
}

GoogleSignIn.propTypes = {
  onCancel: PropTypes.func,
  onError: PropTypes.func,
  onSubmit: PropTypes.func,
  onSuccess: PropTypes.func,
  render: PropTypes.func.isRequired,
  lurker: PropTypes.object,
  tracker: PropTypes.object,
  onPhoneNotValidatedError: PropTypes.func,
  onChallengePinCodeCall: PropTypes.func,
  sessionTokens: PropTypes.object,
  onAccountNotValidated: PropTypes.func,
  onAccountMustValidate: PropTypes.func,
  abTests: PropTypes.string,
  modalMode: PropTypes.bool,
  onAccountCompleteData: PropTypes.func,
}

export default GoogleSignIn
